import React, { useState, useEffect } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const ThresherHarvesterQty = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalQty, setTotalQty] = useState(0);
  const [qtyPerCrop, setQtyPerCrop] = useState([]);
  const [qtyPerState, setQtyPerState] = useState([]);

  const [viewType, setViewType] = useState("crop");
  const [selectedCrop, setSelectedCrop] = useState("all");
  const [selectedState, setSelectedState] = useState("all");
  const [chartType, setChartType] = useState("pie");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const url = `/api/thresherHarvesterQty/?userId=${localStorage.getItem(
      "id"
    )}&orgId=${localStorage.getItem("orgId")}`;

    fetchData({ url }, (res) => {
      setTotalQty(res.totalQty || 0);
      setQtyPerCrop(res.qtyPerCrop || []);
      setQtyPerState(res.qtyPerState || []);
      setLoading(false);
    });
  }, [fetchData]);

  let chartDataset = [];

  if (viewType === "crop") {
    chartDataset =
      selectedCrop === "all"
        ? qtyPerCrop
        : qtyPerCrop.filter((c) => c.cropName === selectedCrop);
  } else {
    chartDataset =
      selectedState === "all"
        ? qtyPerState
        : qtyPerState.filter((s) => s.state === selectedState);
  }

  let displayAmount = totalQty;

  if (viewType === "crop" && selectedCrop !== "all") {
    const rows = qtyPerCrop.filter((c) => c.cropName === selectedCrop);
    displayAmount = rows.reduce((sum, r) => sum + (r.totalQty || 0), 0);
  }

  if (viewType === "state" && selectedState !== "all") {
    const row = qtyPerState.find((s) => s.state === selectedState);
    displayAmount = row ? row.totalQty : 0;
  }

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: "20px",
          background: "#c2d5aa",
          borderRadius: "14px",
        }}
      >
        <div className="flex justify-between mb-5">

          <Select
            value={viewType}
            style={{ width: 180 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedCrop("all");
              setSelectedState("all");
            }}
          >
            <Option value="crop">Crop Wise</Option>
            <Option value="state">State Wise</Option>
          </Select>

          {viewType === "crop" && (
            <Select
              value={selectedCrop}
              style={{ width: 220 }}
              onChange={(v) => setSelectedCrop(v)}
            >
              <Option value="all">All Crops</Option>
              {qtyPerCrop.map((c, i) => (
                <Option key={i} value={c.cropName || "Unknown"}>
                  {c.cropName || "Unknown Crop"}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "state" && (
            <Select
              value={selectedState}
              style={{ width: 220 }}
              onChange={(v) => setSelectedState(v)}
            >
              <Option value="all">All States</Option>
              {qtyPerState.map((s, i) => (
                <Option key={i} value={s.state}>
                  {s.state}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160 }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        <div
          style={{
            textAlign: "center",
            marginBottom: "18px",
            padding: "14px",
            background: "#d6efd3",
            borderRadius: "12px",
          }}
        >
          <h1
            style={{
              fontSize: "40px",
              fontWeight: "700",
              color: "#1f4e1e",
            }}
          >
            {displayAmount.toLocaleString()} Kg
          </h1>

          <p
            style={{
              fontSize: "18px",
              color: "#1f4e1e",
            }}
          >
            {viewType === "crop"
              ? selectedCrop === "all"
                ? "Thresher & Harvester Qty"
                : "Crop-wise Quantity"
              : selectedState === "all"
                ? "Thresher & Harvester Qty"
                : "State-wise Quantity"}
          </p>
        </div>

        {chartType === "pie" && (
          <DynamicPieChart
            data={chartDataset}
            angleField="totalQty"
            colorField={viewType === "crop" ? "cropName" : "state"}
            isMaximized={true}
          />
        )}

        {chartType === "column" && (
          <DynamicColumnChart
            data={chartDataset}
            xField={viewType === "crop" ? "cropName" : "state"}
            yField="totalQty"
            seriesField={viewType === "crop" ? "cropName" : "state"}
          />
        )}

        {chartType === "bar" && (
          <DynamicBarChart
            data={chartDataset}
            xField={viewType === "crop" ? "cropName" : "state"}
            yField="totalQty"
            seriesField={viewType === "crop" ? "cropName" : "state"}
          />
        )}

        {chartType === "line" && (
          <DynamicLineChart
            data={chartDataset}
            xField={viewType === "crop" ? "cropName" : "state"}
            yField="totalQty"
            seriesField={viewType === "crop" ? "cropName" : "state"}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: "200px",
        width: "100%",
        padding: "18px",
        position: "relative",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "700",
                color: "#1f4e1e",
              }}
            >
              {totalQty.toLocaleString()} Kg
            </h1>

            <p
              style={{
                fontSize: "17px",
                marginTop: "-6px",
                color: "#1f4e1e",
              }}
            >
              Thresher & Harvester Qty
            </p>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "18px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#1f4e1e",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default ThresherHarvesterQty;
